import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePortalVersion } from '../../contexts/PortalVersionContext.jsx';
import {
  getSession,
  setSession,
  getUserId,
  getCampusId,
  getSessionId,
  getPortalVersion,
} from '../../services/session.js';
import { apiSave } from '../../services/api.js';
import { compareVersion } from '../../utils/compareVersion.js';
import { collectSlides } from '../../data/walkthroughs/index.js';
import { CURRENT_PORTAL_VERSION } from '../../constants/portalVersion.js';
import WalkthroughModal from './WalkthroughModal.jsx';

/**
 * WalkthroughGate — decides whether to render the walkthrough modal.
 *
 * Rules:
 *   - Only when portalVersion === 'v2' (V1 users are unaffected).
 *   - Only when the session's portal_version is below CURRENT_PORTAL_VERSION.
 *     Brand-new users are tagged at create-time with the current version
 *     (see addUser in usersApi.js), so they never see the modal.
 *   - Slides are filtered by the current user's role_weight; if zero slides
 *     remain after filtering, we still bump the user's portal_version
 *     silently so the gate stops checking on subsequent logins.
 *
 * Mounted once near the app root, inside the authenticated branch.
 */
const WalkthroughGate = () => {
  const { portalVersion } = usePortalVersion();
  const [dismissed, setDismissed] = useState(false);

  const userVersion = getPortalVersion() ?? '0.0.0';
  const isV2 = portalVersion === 'v2';
  const userLoggedIn = !!getSession();
  const isBehind = compareVersion(userVersion, CURRENT_PORTAL_VERSION) < 0;
  const shouldEvaluate = isV2 && userLoggedIn && isBehind && !dismissed;

  const slides = useMemo(() => {
    if (!shouldEvaluate) return [];
    return collectSlides(userVersion, CURRENT_PORTAL_VERSION);
  }, [shouldEvaluate, userVersion]);

  const commitVersion = useCallback(async () => {
    const userId = getUserId();
    if (!userId) return { error: 'No active user.' };

    const result = await apiSave({
      table: 'core_user',
      payload: [{user_id: userId, portal_version: CURRENT_PORTAL_VERSION }],
      pKey: 'user_id',
      pVal: userId,
      condition: 'primary',
      method: 'POST',
      campusId: getCampusId(),
      clientId: getSessionId(),
      userId,
    });

    if (result[0]) {
      const s = getSession();
      if (s) {
        s.portal_version = CURRENT_PORTAL_VERSION;
        setSession(s);
      }
      setDismissed(true);
      return { ok: true };
    }
    return { error: result[1] || 'Failed to save progress.' };
  }, []);

  // Silent bump when there are no role-eligible slides — keeps the gate
  // from re-evaluating every login.
  useEffect(() => {
    if (!shouldEvaluate) return;
    if (slides.length > 0) return;
    let cancelled = false;
    commitVersion().then((res) => {
      if (cancelled) return;
      if (res?.error) {
        // Don't surface anything; this branch is invisible to the user.
        // The gate will simply try again on the next mount.
        console.warn('[WalkthroughGate] silent version bump failed:', res.error);
      }
    });
    return () => { cancelled = true; };
  }, [shouldEvaluate, slides.length, commitVersion]);

  if (!shouldEvaluate || slides.length === 0) return null;

  return (
    <WalkthroughModal
      slides={slides}
      onComplete={commitVersion}
      onSkip={commitVersion}
    />
  );
};

export default WalkthroughGate;
